import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useTurnstile } from "@/lib/turnstile";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import {
  AcceptInvitationForm,
  type InvitationDetails as FormInvitationDetails,
} from "@/components/ui/accept-invitation-form";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface InvitationDetails extends FormInvitationDetails {
  expires_at: string;
}

// Cross-tab race: Supabase syncs auth via localStorage, so a second tab
// in the same browser can auto-accept the invite first. The loser's RPC
// returns one of these — the user already has tenant membership, so we
// treat it as success rather than stranding them on an error screen.
const BENIGN_ACCEPT_RACE = /already been used|already a member/i;

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (
    err &&
    typeof err === "object" &&
    "message" in err &&
    typeof (err as { message: unknown }).message === "string"
  ) {
    return (err as { message: string }).message;
  }
  return fallback;
}

export function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = searchParams.get("token");

  const [invitationDetails, setInvitationDetails] =
    useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // All three auth paths on this page (signUp, signInWithPassword,
  // signInWithOtp) hit Supabase's globally-enforced Captcha gate, so
  // each call must carry a captchaToken. Single shared widget; the
  // AcceptInvitationForm renders the slot below the tab panels.
  const turnstile = useTurnstile();
  const captchaRequired = !turnstile.skipped;
  const captchaReady = !captchaRequired || turnstile.captchaToken !== null;
  // Guard so auto-accept fires at most once per authenticated user.
  // Without this:
  // - On success, supabase.auth.refreshSession() triggers an auth state
  //   update → handleAcceptInvitation re-creates → useEffect fires again →
  //   second accept_invitation RPC against an already-accepted invitation
  //   returns 400.
  // - On permanent failure (email mismatch, expired/deleted invitation),
  //   resetting the flag would re-fire the effect over and over.
  // We key the ref on `session.user.id` so a different user signing in
  // (e.g., the page was open while logged in as the wrong account, then
  // they sign in via the form as the invitee) gets a fresh attempt.
  const acceptedForUserRef = useRef<string | null>(null);

  const fetchInvitationDetails = useCallback(async () => {
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "get_invitation_details",
        {
          p_token: token,
        },
      );

      if (rpcError) throw rpcError;
      if (!data || !data[0]) throw new Error("Invitation not found");

      setInvitationDetails(data[0]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load invitation",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleAcceptInvitation = useCallback(async () => {
    if (!token || !session) return;

    setSubmitting(true);
    setError(null);

    try {
      const { data, error: acceptError } = await supabase.rpc(
        "accept_invitation",
        {
          p_token: token,
        },
      );

      const rpcMessage = acceptError?.message;
      // Benign cross-tab race: another tab in this browser auto-accepted
      // (Supabase syncs sessions via localStorage). The user already has
      // tenant membership — go to the dashboard instead of stranding them.
      if (rpcMessage && BENIGN_ACCEPT_RACE.test(rpcMessage)) {
        await supabase.auth.refreshSession();
        setSubmitting(false);
        navigate("/");
        return;
      }

      if (acceptError) throw acceptError;
      if (!data) throw new Error("Failed to accept invitation");

      // Refresh session to get new JWT with tenant_id
      await supabase.auth.refreshSession();

      // Redirect to dashboard
      navigate("/");
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to accept invitation"));
      setSubmitting(false);
    }
  }, [token, session, navigate]);

  // Fetch invitation details on mount
  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    fetchInvitationDetails();
  }, [token, fetchInvitationDetails]);

  // Auto-accept once per authenticated user — see acceptedForUserRef notes
  // above. We don't retry the same user after a failure (the user would be
  // stuck in a 400 loop on permanent errors like email mismatch); a fresh
  // attempt is granted only when a *different* user signs in.
  useEffect(() => {
    if (!session || !invitationDetails) return;
    if (acceptedForUserRef.current === session.user.id) return;
    acceptedForUserRef.current = session.user.id;
    handleAcceptInvitation();
  }, [session, invitationDetails, handleAcceptInvitation]);

  const handleSignUp = async (password: string, confirmPassword: string) => {
    setValidationError(null);

    if (!invitationDetails) return;

    // Validate passwords
    if (password.length < 6) {
      setValidationError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }

    if (captchaRequired && turnstile.captchaToken === null) {
      setError("Please complete the captcha.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: invitationDetails.email,
        password,
        options: {
          // Hook returns null when Turnstile is skipped; Supabase wants
          // undefined for "no captcha".
          captchaToken: turnstile.captchaToken ?? undefined,
        },
      });

      if (signUpError) throw signUpError;
      if (!data.session) {
        throw new Error("Failed to create session after signup");
      }

      // Accept invitation will be triggered by useEffect when session is set
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
      setSubmitting(false);
    } finally {
      // Single-use token — burn it whether we succeeded or not.
      turnstile.reset();
    }
  };

  const handleLogin = async (password: string) => {
    setValidationError(null);

    if (!invitationDetails) return;

    if (captchaRequired && turnstile.captchaToken === null) {
      setError("Please complete the captcha.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { data, error: signInError } =
        await supabase.auth.signInWithPassword({
          email: invitationDetails.email,
          password,
          options: { captchaToken: turnstile.captchaToken ?? undefined },
        });

      if (signInError) throw signInError;
      if (!data.session) {
        throw new Error("Failed to create session after login");
      }

      // Accept invitation will be triggered by useEffect when session is set
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      setSubmitting(false);
    } finally {
      turnstile.reset();
    }
  };

  const handleMagicLink = async () => {
    if (!invitationDetails) return;

    if (captchaRequired && turnstile.captchaToken === null) {
      setError("Please complete the captcha.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const redirectUrl = `${window.location.origin}/accept-invitation?token=${token}`;

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: invitationDetails.email,
        options: {
          emailRedirectTo: redirectUrl,
          captchaToken: turnstile.captchaToken ?? undefined,
        },
      });

      if (otpError) throw otpError;

      setError(null);
      alert(
        "Magic link sent! Check your email and click the link to continue.",
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send magic link",
      );
    } finally {
      turnstile.reset();
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (error && !invitationDetails) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate("/login")} className="w-full">
              Go to Login
            </Button>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  if (!invitationDetails) {
    return null;
  }

  const isExpired = new Date(invitationDetails.expires_at) <= new Date();

  if (isExpired) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invitation Expired</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                This invitation expired on{" "}
                {new Date(invitationDetails.expires_at).toLocaleDateString()}.
                Please contact the team owner for a new invitation.
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate("/login")} className="w-full">
              Go to Login
            </Button>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  // If user is authenticated, either the auto-accept useEffect is in
  // flight (spinner) or it failed with a non-benign error (show it).
  if (session) {
    if (error) {
      return (
        <AuthLayout>
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Couldn&apos;t accept invitation</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <Button
                onClick={() => {
                  // Clear the per-user guard so a future session change
                  // can re-trigger auto-accept, and re-invoke directly
                  // (useEffect deps don't change, so it won't re-fire).
                  acceptedForUserRef.current = null;
                  handleAcceptInvitation();
                }}
                className="w-full"
              >
                Try again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </CardFooter>
          </Card>
        </AuthLayout>
      );
    }
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center gap-4 p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-center text-muted-foreground">
              Accepting invitation...
            </p>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  const captchaSlot = captchaRequired ? (
    <>
      <div
        ref={turnstile.ref}
        data-testid="turnstile-widget"
        className="flex justify-center"
      />
      {turnstile.failed && (
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>
            Couldn&apos;t load the security check. Please refresh the page or
            check your network connection.
          </AlertDescription>
        </Alert>
      )}
    </>
  ) : null;

  return (
    <AuthLayout>
      <AcceptInvitationForm
        invitationDetails={invitationDetails}
        onSignUp={handleSignUp}
        onLogin={handleLogin}
        onMagicLink={handleMagicLink}
        loading={submitting}
        error={error}
        validationError={validationError}
        captchaSlot={captchaSlot}
        submitDisabled={!captchaReady}
      />
    </AuthLayout>
  );
}
